import { produce, Draft } from 'immer'
import findIndex from 'lodash/findIndex'
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import find from 'lodash/find'

import {
  IAction,
  TActionTypes,
} from '@Root/types'

import {
  ProjectVersionSettingsActionTypes,
  IProjectVersionSettingsState,
  TProjectVersionSettingsActionTypes,
  IFetchProjectVersionSettingsSuccessResponseBody,
  IProjectVersionSettingsAttributes,
  IAvailableLanguagesAttributes,
  ILastUsedSettingsAttributes,
  IAddElementsForProjectVersionsPayload,
  IRemoveFromElementsForProjectVersionsPayload,
  ISetValuesAfterUploadPayload,
  IAddProjectIdPendingCleanupPayload,
  IRemoveFromSubmittingUrlsPayload,
  IUpdateDownloadingElementsSuccessResponseBody,
  IFetchTranslationLanguagesSuccessResponseBody,
} from './types'

import {
  ElementActionTypes,
  TElementActionTypes,
  ISubmitUrlsPayload,
} from '@SiteModules/Element/types'

import {
  OnboardingActionTypes,
  TOnboardingActionTypes,
  IFetchQuickstartSettingsSuccessResponseBody
} from '@SiteModules/Onboarding/types'

const initialSettings: IProjectVersionSettingsAttributes = {
  duration: 0,
  language: '',
  subtitleLanguages: [],
  hasAudioSeparation: false,
  subscriptionSubtitlesLength: 0,
  hasVoice: false,
}

const initialAvailableLanguages: IAvailableLanguagesAttributes = {
  languagesForVideos: [],
  translationLanguages: [],
  nonTranslatableLanguages: [],
  alignmentLanguages: [],
  dubbingLanguages: [],
  voiceCloningLanguages: [],
}

const initialLastUsedSettings: ILastUsedSettingsAttributes = {
}

export const initialState: IProjectVersionSettingsState = {
  settings: initialSettings,
  availableLanguages: initialAvailableLanguages,
  lastUsedSettings: initialLastUsedSettings,
  hasLoadedSuccessfully: false,
  elementsForProjectVersions: [],
  projectIdsPendingCleanup: [],
  projectCleanupOwnership: 'setup',
  fetchingTranslationLanguages: false,
  submitUrl: {
    showSizeLimitsAlert: false,
    submittingUrls: []
  }
}

type TAction = TActionTypes | TProjectVersionSettingsActionTypes | TElementActionTypes | TOnboardingActionTypes

export const projectVersionSettingsReducer = (
  state: IProjectVersionSettingsState = initialState,
  action: TAction
): IProjectVersionSettingsState => {
  return produce(state, draft => {
    switch (action.type) {
      case ProjectVersionSettingsActionTypes.SUBMIT_PROJECT_VERSION_UPDATE:
      case ProjectVersionSettingsActionTypes.BEGIN_PROJECT_SUBMISSION:
        transitionToProjectSubmission(draft)
        break

      case ProjectVersionSettingsActionTypes.SUBMIT_PROJECT_VERSION_UPDATE_SUCCESS:
      case ProjectVersionSettingsActionTypes.COMPLETE_PROJECT_SUBMISSION:
        transitionToCompletedProjectSubmission(draft)
        break

      case ProjectVersionSettingsActionTypes.SUBMIT_PROJECT_VERSION_UPDATE_FAILURE:
      case ProjectVersionSettingsActionTypes.FAIL_PROJECT_SUBMISSION:
        transitionToFailedProjectSubmission(draft)
        break

      case ProjectVersionSettingsActionTypes.FETCH_PROJECT_VERSION_SETTINGS:
      case OnboardingActionTypes.FETCH_QUICKSTART_SETTINGS:
        fetchProjectVersionSettings(draft)
        break

      case ProjectVersionSettingsActionTypes.FETCH_PROJECT_VERSION_SETTINGS_SUCCESS:
        fetchProjectVersionSettingsSuccess(draft, action)
        break

      case ProjectVersionSettingsActionTypes.PROJECT_VERSION_SETTINGS_FAILURE:
      case OnboardingActionTypes.FETCH_QUICKSTART_SETTINGS_FAILURE:
        projectVersionSettingsFailure(draft)
        break

      case ProjectVersionSettingsActionTypes.RESET_PROJECT_VERSION_SETTINGS:
        resetProjectVersionSettings(draft)
        break

      case ProjectVersionSettingsActionTypes.ADD_ELEMENTS_FOR_PROJECT_VERSIONS:
        addElementsForProjectVersions(draft, action)
        break

      case ProjectVersionSettingsActionTypes.REMOVE_FROM_ELEMENTS_FOR_PROJECT_VERSIONS:
        removeFromElementsForProjectVersions(draft, action)
        break

      case ProjectVersionSettingsActionTypes.SET_VALUES_AFTER_UPLOAD:
        setValuesAfterUpload(draft, action)
        break

      case ProjectVersionSettingsActionTypes.ADD_PROJECT_ID_PENDING_CLEANUP:
        addProjectIdPendingCleanup(draft, action)
        break

      case ProjectVersionSettingsActionTypes.RELEASE_PROJECT_CLEANUP_OWNERSHIP:
        releaseProjectCleanupOwnership(draft)
        break

      case ProjectVersionSettingsActionTypes.SET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL:
        setSizeLimitsAlertAtSubmitUrl(draft)
        break

      case ProjectVersionSettingsActionTypes.RESET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL:
        resetSizeLimitsAlertAtSubmitUrl(draft)
        break

      case ElementActionTypes.SUBMIT_URLS:
        addToSubmittingUrls(draft, action)
        break

      case ProjectVersionSettingsActionTypes.REMOVE_FROM_SUBMITTING_URLS:
        removeFromSubmittingUrls(draft, action)
        break

      case ProjectVersionSettingsActionTypes.RESET_SUBMITTING_URLS:
        resetSubmittingUrls(draft)
        break

      case ProjectVersionSettingsActionTypes.UPDATE_DOWNLOADING_ELEMENTS_SUCCESS:
        updateDownloadingElementsSuccess(draft, action)
        break

      case ProjectVersionSettingsActionTypes.FETCH_TRANSLATION_LANGUAGES:
        fetchTranslationLanguages(draft)
        break

      case ProjectVersionSettingsActionTypes.FETCH_TRANSLATION_LANGUAGES_SUCCESS:
        fetchTranslationLanguagesSuccess(draft, action)
        break

      case ProjectVersionSettingsActionTypes.FETCH_TRANSLATION_LANGUAGES_FAILURE:
        fetchTranslationLanguagesFailure(draft)
        break

      case ProjectVersionSettingsActionTypes.RESET_TRANSLATION_LANGUAGES:
        resetTranslationLanguages(draft)
        break

      case OnboardingActionTypes.FETCH_QUICKSTART_SETTINGS_SUCCESS:
        fetchQuickstartSettingsSuccess(draft, action)
        break

      default:
        break
    }
  })
}

const fetchProjectVersionSettings = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.hasLoadedSuccessfully = false
}

function isIFetchProjectVersionSettingsSuccessAction(arg: any): arg is IAction<IFetchProjectVersionSettingsSuccessResponseBody> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.FETCH_PROJECT_VERSION_SETTINGS_SUCCESS
  )
}

const fetchProjectVersionSettingsSuccess = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIFetchProjectVersionSettingsSuccessAction(action)) { return }

  draft.settings = { ...initialSettings, ...action.payload.projectVersionSettings.attributes }
  draft.availableLanguages = action.payload.availableLanguages.attributes
  draft.lastUsedSettings = action.payload.lastUsedSettings.attributes
  draft.hasLoadedSuccessfully = true
}

const projectVersionSettingsFailure = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.hasLoadedSuccessfully = false
}

const resetProjectVersionSettings = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.hasLoadedSuccessfully = false
  draft.settings = initialSettings
  draft.availableLanguages = initialAvailableLanguages
  draft.lastUsedSettings = initialLastUsedSettings
  draft.elementsForProjectVersions = []
  draft.projectIdsPendingCleanup = []
  draft.projectCleanupOwnership = 'setup'
  draft.submitUrl.showSizeLimitsAlert = false
  draft.submitUrl.submittingUrls = []
}

function isIAddElementsForProjectVersionsAction(arg: any): arg is IAction<IAddElementsForProjectVersionsPayload> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.ADD_ELEMENTS_FOR_PROJECT_VERSIONS
  )
}

const addElementsForProjectVersions = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIAddElementsForProjectVersionsAction(action)) { return }

  draft.elementsForProjectVersions = draft.elementsForProjectVersions.concat(action.payload.elementsToAdd)
  action.payload.elementsToAdd.forEach(element => {
    if (element.projectId && !draft.projectIdsPendingCleanup.includes(element.projectId)) {
      draft.projectIdsPendingCleanup.push(element.projectId)
    }
  })
}

function isIRemoveFromElementsForProjectVersionsAction(arg: any): arg is IAction<IRemoveFromElementsForProjectVersionsPayload> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.REMOVE_FROM_ELEMENTS_FOR_PROJECT_VERSIONS
  )
}

const removeFromElementsForProjectVersions = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIRemoveFromElementsForProjectVersionsAction(action)) { return }

  draft.elementsForProjectVersions = filter(draft.elementsForProjectVersions, elementForProjectVersion => elementForProjectVersion.elementId !== action.payload.elementId)
}

function isISetValuesAfterUploadAction(arg: any): arg is IAction<ISetValuesAfterUploadPayload> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.SET_VALUES_AFTER_UPLOAD
  )
}

const setValuesAfterUpload = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isISetValuesAfterUploadAction(action)) { return }

  const elementToUpdateIndex = findIndex(draft.elementsForProjectVersions, element => element.elementId === action.payload.elementId)

  if (elementToUpdateIndex >= 0) {
    draft.elementsForProjectVersions[elementToUpdateIndex].duration = action.payload.duration
    draft.elementsForProjectVersions[elementToUpdateIndex].projectVersionId = action.payload.projectVersionId
    draft.elementsForProjectVersions[elementToUpdateIndex].projectId = action.payload.projectId
    if (!draft.projectIdsPendingCleanup.includes(action.payload.projectId)) {
      draft.projectIdsPendingCleanup.push(action.payload.projectId)
    }
  }
}

function isIAddProjectIdPendingCleanupAction(arg: any): arg is IAction<IAddProjectIdPendingCleanupPayload> {
  return arg.type === ProjectVersionSettingsActionTypes.ADD_PROJECT_ID_PENDING_CLEANUP
}

const addProjectIdPendingCleanup = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIAddProjectIdPendingCleanupAction(action)) { return }

  if (!draft.projectIdsPendingCleanup.includes(action.payload.projectId)) {
    draft.projectIdsPendingCleanup.push(action.payload.projectId)
  }
}

const releaseProjectCleanupOwnership = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.projectIdsPendingCleanup = []
  draft.projectCleanupOwnership = 'released'
}

const transitionToProjectSubmission = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.projectCleanupOwnership = 'submission'
}

const transitionToCompletedProjectSubmission = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.projectIdsPendingCleanup = []
  draft.projectCleanupOwnership = 'released'
}

const transitionToFailedProjectSubmission = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.projectCleanupOwnership = 'setup'
}

const setSizeLimitsAlertAtSubmitUrl = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.submitUrl.showSizeLimitsAlert = true
}

const resetSizeLimitsAlertAtSubmitUrl = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.submitUrl.showSizeLimitsAlert = false
}

function isISubmitUrlsAction(arg: any): arg is IAction<ISubmitUrlsPayload> {
  return (
    arg.type === ElementActionTypes.SUBMIT_URLS
  )
}

const addToSubmittingUrls = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isISubmitUrlsAction(action)) { return }

  draft.submitUrl.submittingUrls.push({ url: action.payload.urls[0], identifier: action.payload.submitUrlIdentifier })
}

function isIRemoveFromSubmittingUrlsAction(arg: any): arg is IAction<IRemoveFromSubmittingUrlsPayload> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.REMOVE_FROM_SUBMITTING_URLS
  )
}

const removeFromSubmittingUrls = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIRemoveFromSubmittingUrlsAction(action)) { return }

  draft.submitUrl.submittingUrls = filter(draft.submitUrl.submittingUrls, submittingUrl => submittingUrl.identifier !== action.payload.submitUrlIdentifier)
}

const resetSubmittingUrls = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.submitUrl.submittingUrls = []
}

function isIUpdateDownloadingElementsSuccessAction(arg: any): arg is IAction<IUpdateDownloadingElementsSuccessResponseBody> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.UPDATE_DOWNLOADING_ELEMENTS_SUCCESS
  )
}

const updateDownloadingElementsSuccess = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIUpdateDownloadingElementsSuccessAction(action)) { return }

  forEach(draft.elementsForProjectVersions, elementForProjectVersion => {
    if (!!elementForProjectVersion.downloading) {
      const updatedElement = find(action.payload.data, elementData => elementData.id === elementForProjectVersion.elementId)
      if (updatedElement?.attributes.thumbnailUrl) {
        elementForProjectVersion.thumbnailUrl = updatedElement.attributes.thumbnailUrl
      }

      if (updatedElement && updatedElement.attributes.state !== 'downloading') {
        elementForProjectVersion.downloading = false
        elementForProjectVersion.size = updatedElement.attributes.unformattedSize
        elementForProjectVersion.duration = updatedElement.attributes.unformattedDuration
      }
    }
  })
}

const fetchTranslationLanguages = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.fetchingTranslationLanguages = true
}

function isIFetchTranslationLanguagesSuccessAction(arg: any): arg is IAction<IFetchTranslationLanguagesSuccessResponseBody> {
  return (
    arg.type === ProjectVersionSettingsActionTypes.FETCH_TRANSLATION_LANGUAGES_SUCCESS
  )
}

const fetchTranslationLanguagesSuccess = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIFetchTranslationLanguagesSuccessAction(action)) { return }

  draft.availableLanguages.translationLanguages = action.payload.translationLanguages
  draft.fetchingTranslationLanguages = false
}

const fetchTranslationLanguagesFailure = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.availableLanguages.translationLanguages = []
  draft.fetchingTranslationLanguages = false
}

const resetTranslationLanguages = (draft: Draft<IProjectVersionSettingsState>) => {
  draft.availableLanguages.translationLanguages = []
}

function isIFetchQuickstartSettingsSuccessAction(arg: any): arg is IAction<IFetchQuickstartSettingsSuccessResponseBody> {
  return (
    arg.type === OnboardingActionTypes.FETCH_QUICKSTART_SETTINGS_SUCCESS
  )
}

const fetchQuickstartSettingsSuccess = (draft: Draft<IProjectVersionSettingsState>, action: TAction) => {
  if (!isIFetchQuickstartSettingsSuccessAction(action)) { return }

  draft.availableLanguages.languagesForVideos = action.payload.availableLanguages.attributes.languagesForVideos
  draft.availableLanguages.translationLanguages = action.payload.availableLanguages.attributes.translationLanguages
  draft.availableLanguages.dubbingLanguages = action.payload.availableLanguages.attributes.dubbingLanguages
  draft.availableLanguages.voiceCloningLanguages = action.payload.availableLanguages.attributes.voiceCloningLanguages
  draft.hasLoadedSuccessfully = true
}
